import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {Navigate, Route, Routes} from 'react-router-dom';

import {ErrorBoundary} from '@sentry/react';
import {ACCOUNT_TABS} from 'src/constants';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {ErrorPage} from 'src/modules/error-page/error-page.components';
import {selectAccountDetails} from 'src/modules/profile/account/services/account-page-slice';
import {getActiveAccountProfileLicense} from 'src/openapi/admin/apis/AccountActiveLicenseApi';
import {useAppDispatch} from 'src/store/hooks';

import Loading from '@pb/kit-component-loading';

import {LicenseFeaturesTabView} from './license-features-tab.view';
import {NoLicenseFeaturesTabView} from './no-license-features-tab.view';

type Props = {
    accountId: number,
};

export const LicenseFeaturesTab = ({accountId}: Props) => {
    const [loading, setLoading] = useState(false);

    const apiDispatch = useAppDispatch();

    const {hasAccess} = useAdminAccessRight();

    const {license: nearestLicense} = useSelector(selectAccountDetails);

    const canViewLicense = hasAccess(ACCESS_RIGHTS.LICENSE_SCREEN_VIEW);

    useEffect(() => {
        if (!nearestLicense && accountId && canViewLicense) {
            setLoading(true);
            apiDispatch(getActiveAccountProfileLicense({accountId: +accountId})).then(response => {
                if (response.payload.success) {
                    setLoading(false);
                }
            });
        }
    }, [nearestLicense, accountId, canViewLicense, apiDispatch]);

    if (!canViewLicense) {
        return (
            <ErrorPage handler="/" title="Access Denied" subtitle="You have no access to Features and Limits "/>
        );
    }

    if (loading) {
        return <Loading fast={true}/>;
    }

    if (!nearestLicense?.licenseId) {
        return (
            <ErrorBoundary fallback={<ErrorPage subtitle="Please try again later." title="License page is unavailable"/>}>
                <NoLicenseFeaturesTabView accountId={accountId}/>
            </ErrorBoundary>
        );
    }

    return (
        <ErrorBoundary fallback={<ErrorPage subtitle="Please try again later." title="License page is unavailable"/>}>
            <Routes>
                <Route
                    path="/:licenseId/*"
                    element={<LicenseFeaturesTabView/>}
                />

                <Route
                    path="*"
                    element={(
                        <Navigate to={
                            `/profile/account/${accountId}/${ACCOUNT_TABS.LICENSE.name}/${nearestLicense?.licenseId}`
                        }
                        />
                    )}
                />
            </Routes>
        </ErrorBoundary>
    );
};
